import axios from "axios";
const BASE_URL: string = process.env.REACT_APP_API_URL as string;
const axiosInstance = axios.create({ baseURL: BASE_URL, withCredentials: true });

let refreshPromise: Promise<void> | null = null;
let refreshFailed = false;

const DEAD_SESSION = new Set(["REFRESH_TOKEN_REQUIRED", "REFRESH_TOKEN_INVALID", "REFRESH_TOKEN_EXPIRED", "USER_INACTIVE", "USER_NOT_FOUND"]);

const isDeadSession = (error: any) => DEAD_SESSION.has(error?.response?.data?.error);

const clearAuth = () => {
  localStorage.removeItem("userId");
  localStorage.removeItem("userData");
  localStorage.removeItem("token");
  localStorage.removeItem("refreshToken");
};

export const persistAuthTokens = (accessToken?: string | null, refreshToken?: string | null) => {
  refreshFailed = false;
  if (accessToken) localStorage.setItem("token", accessToken);
  if (refreshToken) localStorage.setItem("refreshToken", refreshToken);
};

const forceLogout = () => {
  refreshFailed = true;
  clearAuth();
  window.location.replace("/");
};

const refreshAccessToken = async () => {
  const refreshToken = localStorage.getItem("refreshToken");
  const response = await axiosInstance.post("/api/v1/users/refresh", {
    device_type: "web",
    ...(refreshToken ? { refreshToken } : {}),
  });
  persistAuthTokens(response?.data?.token, response?.data?.refreshToken);
};

const refreshSession = () => {
  if (!refreshPromise) {
    refreshPromise = refreshAccessToken().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

// errorComposer will compose a handleGlobally function
const errorComposer = (error) => {
  const statusCode = error.response ? error.response.status : null;
  let message = "Something went wrong.";
  if (statusCode === 404) {
    message = "The requested resource does not exist or has been deleted";
  }

  if (statusCode === 401) {
    message = "Please login to access this resource";
  }
  if (statusCode === 400) {
    message = error.response;
  }
  return message;
};

//Handles the error
const errHandling = (error) => {
  return Promise.reject(error);
};

//Handles the request
const reqHandling = (config) => {
  return new Promise((resolve, reject) => {
    const obj = { ...config };
    const token = localStorage.getItem("token");
    if (token) {
      obj.headers = obj.headers || {};
      obj.headers["Authorization"] = "Bearer " + token;
    }
    resolve(obj);
  });
};

const resHandling = (response) => response;
axiosInstance.interceptors.request.use(reqHandling, errHandling);
axiosInstance.interceptors.response.use(resHandling, async (error) => {
  const originalRequest = error.config || {};
  const url = String((originalRequest as any)?.url || "");
  const errCode = (error as any)?.response?.data?.error;

  // Admin deactivated this account — force logout, do not attempt refresh.
  if ((error as any)?.response?.status === 401 && (errCode === "USER_INACTIVE" || errCode === "USER_NOT_FOUND")) {
    forceLogout();
    return Promise.reject(error);
  }

  // Never try to "refresh by calling refresh" (prevents infinite loops)
  if (url.includes("/api/v1/users/refresh") || url.includes("/api/v1/users/logout")) {
    if (error?.response?.status === 401 && isDeadSession(error)) {
      forceLogout();
    }
    return Promise.reject(error);
  }

  if (error?.response?.status === 401 && !originalRequest._retry) {
    originalRequest._retry = true;

    if (!localStorage.getItem("userData")) return Promise.reject(error);
    if (refreshFailed) {
      forceLogout();
      return Promise.reject(error);
    }

    try {
      await refreshSession();
      return axiosInstance(originalRequest);
    } catch (e: any) {
      if (isDeadSession(e)) forceLogout();
      return Promise.reject(error);
    }
  }

  return Promise.reject(error);
});

export default axiosInstance;
